/**
 * Où lire l'image d'un widget Image ENREGISTRÉ.
 *
 * Les routes legacy (`/multipleWidgets`, `PATCH /widget/:id`) déplaçaient le fichier téléversé de
 * `workingWidgets/<temporaryImageId>` vers `widgets/<id du widget>`. Depuis le lot 3 dashboards v2
 * l'écran enregistre par le GraphQL v2, qui n'a pas accès au disque du back legacy : le widget
 * désigne son fichier (`imageFile`, posé par `prepareSave`). Sans `imageFile` : l'emplacement legacy.
 *
 * Le répertoire part dans une URL de lecture du back legacy — seules ses deux valeurs connues passent.
 */
const IMAGE_DIRECTORIES = ["workingWidgets", "widgets"]

/** Image du stockage GRC (lot 5) : `imageFile: { storage: "v2", fileId }`, lue sur le back GRC. */
const isV2File = file => file?.storage === "v2" && Boolean(file?.fileId)

export const savedWidgetImage = (widget, widgetId) => {
  const file = widget?.imageFile
  if (isV2File(file)) return { storage: "v2", fileId: String(file.fileId) }
  if (file?.id && IMAGE_DIRECTORIES.includes(file.directory)) return { directory: file.directory, id: String(file.id) }
  return { directory: "widgets", id: widgetId }
}

/**
 * Image du widget à charger, ou `null` : la même clé tant que l'image ne change pas (une adresse
 * résolue pour une autre image ne doit jamais s'afficher).
 * - widget enregistré : son fichier (`savedWidgetImage`) ;
 * - widget neuf (`temporaryId`) dont l'aperçu n'est pas encore là : le fichier téléversé.
 */
export const imageToLoad = (widget, widgetId) => {
  if (widget?.widgetType !== "image" || !widget?.isUploadedImage || !widgetId) return null
  const props = widget?.widgetProps || {}
  // Image du stockage GRC (lot 5) : la référence désigne le fichier, quel que soit l'id — copie du
  // panneau de réglages comprise. Aperçu local `blob:` encore là : rien à charger ; vidé
  // (enregistrement, brouillon repris) : le fichier, lu sur la route v2.
  if (isV2File(widget?.imageFile)) {
    const fileId = String(widget.imageFile.fileId)
    if (props.isTempImage && props.tempUrlImage) return null
    return { prop: props.isTempImage ? "tempUrlImage" : "urlImage", storage: "v2", fileId, key: `v2/${fileId}` }
  }
  // Copie du panneau de réglages : son id n'est pas celui du widget — seul un fichier DÉSIGNÉ
  // (`imageFile`, posé par `withImageFileOf` à l'ouverture, ou le téléversement en cours) se lit.
  if (widgetId === "widgetBeingEdited") {
    const tempFile = widget?.temporaryImageId ? String(widget.temporaryImageId) : null
    if (props.isTempImage && tempFile && !props.tempUrlImage) {
      return { prop: "tempUrlImage", directory: "workingWidgets", id: tempFile, key: `workingWidgets/${tempFile}` }
    }
    const file = widget?.imageFile
    if (props.isTempImage || !file?.id || !IMAGE_DIRECTORIES.includes(file.directory)) return null
    return { prop: "urlImage", directory: file.directory, id: String(file.id), key: `${file.directory}/${file.id}` }
  }
  // Image téléversée mais pas encore enregistrée, sans aperçu (brouillon repris) : SON fichier,
  // `workingWidgets/<temporaryImageId>` — pas l'id du widget, sous lequel rien n'est téléversé.
  const tempFile = widget?.temporaryImageId ? String(widget.temporaryImageId) : null
  if (props.isTempImage && tempFile && !props.tempUrlImage) {
    return { prop: "tempUrlImage", directory: "workingWidgets", id: tempFile, key: `workingWidgets/${tempFile}` }
  }
  if (!Object.prototype.hasOwnProperty.call(widget, "temporaryId")) {
    const image = savedWidgetImage(widget, widgetId)
    return { prop: "urlImage", directory: image.directory, id: image.id, key: `${image.directory}/${image.id}` }
  }
  if (!props.tempUrlImage) {
    const id = tempFile || widgetId
    return { prop: "tempUrlImage", directory: "workingWidgets", id, key: `workingWidgets/${id}` }
  }
  return null
}

/**
 * Clé complète d'une image à demander, ou `null` tant qu'il manque de quoi la demander.
 * - v1 : l'adresse legacy porte le client (`/images/<dir>/<businessId>/<id>`) — demandée avant que
 *   le client soit connu, `/images/…/undefined/…` rendait une erreur gardée comme image ;
 * - v2 : le client vient du JETON (back GRC) ; c'est la PAGE qui est exigée (`/images/dashboard/<pageId>/<fileId>`).
 */
export const widgetImageRequestKey = (wanted, { pageId, businessId, token } = {}) => {
  if (!wanted || !token) return null
  if (wanted.storage === "v2") return pageId ? `${pageId}/${wanted.key}` : null
  return businessId ? `${businessId}/${wanted.key}` : null
}

/**
 * Brouillon repris : une adresse `blob:` ne survit pas au rechargement de la page. Vidée, elle est
 * rechargée depuis le fichier (`imageToLoad`) ; une image en base64 (pas encore téléversée), elle, reste.
 */
export const withoutDeadImageUrls = widgets =>
  widgets.map(widget => {
    if (widget?.widgetType !== "image") return widget
    const props = widget?.widgetProps || {}
    const dead = key => typeof props[key] === "string" && props[key].startsWith("blob:")
    if (!dead("tempUrlImage") && !dead("urlImage")) return widget
    return {
      ...widget,
      widgetProps: { ...props, ...(dead("tempUrlImage") && { tempUrlImage: "" }), ...(dead("urlImage") && { urlImage: "" }) },
    }
  })

/**
 * Le widget à AFFICHER, avec l'adresse résolue (état de la carte) posée dans ses propriétés si
 * elles n'en ont pas. Avant : l'adresse était écrite dans l'objet du widget APRÈS coup, sans rendu —
 * un widget image enregistré restait « non configuré » au rechargement (vu le 2026-10-30).
 */
export const withResolvedImage = (widget, widgetId, resolved) => {
  const wanted = imageToLoad(widget, widgetId)
  if (!wanted || !resolved || resolved.key !== wanted.key || !resolved.url) return widget
  if (widget?.widgetProps?.[wanted.prop]) return widget
  return { ...widget, widgetProps: { ...widget.widgetProps, [wanted.prop]: resolved.url } }
}

/**
 * Widget ouvert dans le panneau de réglages : la copie perd l'id du widget, et avec lui
 * l'emplacement legacy de son image (`widgets/<id>`). Il est désigné AVANT, pour que l'aperçu
 * l'affiche (vu le 2026-10-30 : aperçu vide). Un widget neuf (`temporaryId`) n'a rien d'enregistré.
 */
export const withImageFileOf = (widget, widgetId) => {
  if (widget?.widgetType !== "image" || !widget?.isUploadedImage || !widgetId) return widget
  if (Object.prototype.hasOwnProperty.call(widget, "temporaryId")) return widget
  // Image du stockage GRC (lot 5) : déjà désignée — la réécrire en `widgets/<id>` la perdrait.
  if (widget?.imageFile?.storage === "v2") return widget
  return { ...widget, imageFile: savedWidgetImage(widget, widgetId) }
}
